import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, TrendingUp, DollarSign, Package, ShoppingCart, Users, Calendar, CreditCard, BarChart3, PieChart, TrendingDown, Percent, Wallet, Tag } from "lucide-react";
import { format, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { useTenant } from "@/contexts/TenantContext";

type ProductCategory = {
  id: string;
  nome: string;
  descricao: string | null;
  cor_hex: string | null;
  icone: string | null;
  status: string | null;
  foto_url: string | null;
  created_at: string;
};

type Product = {
  id: string;
  nome: string;
  codigo_sku: string | null;
  descricao: string | null;
  preco_venda: number;
  preco_custo: number | null;
  status: string;
  foto_url: string | null;
  product_category_id: string | null;
  created_at: string;
};

type TransactionItem = {
  id: string;
  quantidade: number;
  preco_unitario: number;
  total: number;
  created_at: string;
  transaction_id: string;
  product_id: string;
  transactions: {
    id: string;
    tipo: string;
    valor_total: number;
    valor_liquido: number;
    status: string;
    created_at: string;
    client_id: string | null;
    vendedor_id: string | null;
    payment_method_id: string | null;
    clients: {
      id: string;
      nome: string;
    } | null;
  };
  products: {
    id: string;
    nome: string;
  } | null;
};

type CategoryStats = {
  totalProducts: number;
  totalRevenue: number;
  totalQuantity: number;
  totalTransactions: number;
  averagePrice: number;
  averageQuantity: number;
  maxPrice: number;
  minPrice: number;
  totalProfit: number;
  profitMargin: number;
  firstSale: string | null;
  lastSale: string | null;
  monthlySales: { month: string; revenue: number; quantity: number; count: number }[];
  paymentMethods: { method: string; value: number; count: number }[];
  topProducts: { product: string; revenue: number; quantity: number }[];
  topClients: { client: string; revenue: number; quantity: number }[];
  statusDistribution: { status: string; count: number; revenue: number }[];
  weeklyDistribution: { day: string; revenue: number; quantity: number }[];
  growthTrend: number;
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { 
    style: "currency", 
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

export default function ProductCategoryDetails(props?: { id?: string; embedded?: boolean; periodRange?: [Date, Date]; setLoading?: (loading: boolean) => void }) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = props?.id ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useTenant();
  const search = new URLSearchParams(location.search);
  const isEmbedded = (typeof props?.embedded !== 'undefined') ? !!props?.embedded : (search.get("embedded") === "1");
  const fromStr = search.get("from");
  const toStr = search.get("to");
  const [cadFrom, setCadFrom] = useState<Date>(props?.periodRange ? startOfDay(props.periodRange[0]) : (fromStr ? startOfDay(new Date(fromStr)) : startOfDay(new Date(0))));
  const [cadTo, setCadTo] = useState<Date>(props?.periodRange ? endOfDay(props.periodRange[1]) : (toStr ? endOfDay(new Date(toStr)) : endOfDay(new Date())));
  useEffect(() => {
    if (isEmbedded && !props?.periodRange) {
      const handler = (e: MessageEvent) => {
        const data = (e.data || {}) as any;
        if (data && data.type === 'rx-period-change') {
          setCadFrom(startOfDay(new Date(data.from)));
          setCadTo(endOfDay(new Date(data.to)));
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
  }, [isEmbedded, props?.periodRange]);

  const { data: category, isLoading: categoryLoading } = useQuery({
    queryKey: ["product-category", id, tenantId],
    queryFn: async () => {
      if (!tenantId || !id) return null;
      try {
        const { data, error } = await (supabase as any)
          .from("product_categories")
          .select("*")
          .eq("tenant_id", tenantId)
          .eq("id", id)
          .maybeSingle();
        if (error) {
          if (error.code === 'PGRST116') {
            return null;
          }
          console.error("Erro ao buscar categoria:", error);
          return null;
        }
        return (data || null) as any;
      } catch (err) {
        console.error("Erro ao buscar categoria:", err);
        return null;
      }
    },
    enabled: !!id && !!tenantId,
    retry: false,
  });

  const categoryName = category?.nome || "";

  // Buscar produtos da categoria
  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ["category-products", categoryName, tenantId],
    queryFn: async () => {
      if (!tenantId || !categoryName) return [];
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("tenant_id", tenantId)
        .eq("product_category_id", id);
      if (error) throw error;
      return (data || []) as Product[];
    },
    enabled: !!categoryName && !!tenantId,
  });

  // Buscar transaction_items dos produtos da categoria
  const { data: transactionItems = [], isLoading: itemsLoading } = useQuery({
    queryKey: ["category-transaction-items", products.map(p => p.id), tenantId, cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      if (!tenantId || !products.length) return [];
      const productIds = products.map(p => p.id);
      const { data, error } = await supabase
        .from("transaction_items")
        .select(`
          *,
          transactions!inner (
            id,
            tipo,
            valor_total,
            valor_liquido,
            status,
            created_at,
            client_id,
            vendedor_id,
            payment_method_id,
            clients (id, nome)
          ),
          products (id, nome)
        `)
        .in("product_id", productIds)
        .eq("transactions.tipo", "entrada")
        .gte("transactions.created_at", cadFrom.toISOString())
        .lte("transactions.created_at", cadTo.toISOString())
        .order("created_at", { foreignTable: "transactions", ascending: false });
      if (error) throw error;
      return (data || []) as TransactionItem[];
    },
    enabled: !!products.length && !!tenantId,
  });

  useEffect(() => {
    const loading = categoryLoading || productsLoading || itemsLoading;
    if (isEmbedded) {
      window.parent?.postMessage({ type: 'rx-loading', loading }, '*');
    }
    if (props?.setLoading) props.setLoading(loading);
  }, [categoryLoading, productsLoading, itemsLoading, isEmbedded, props?.setLoading]);

  // Buscar métodos de pagamento
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ["payment-methods", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("payment_methods")
        .select("id, nome");
      if (error) {
        console.error("Erro ao buscar métodos de pagamento:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!tenantId,
  });

  // Buscar clientes
  const { data: clients = [] } = useQuery({
    queryKey: ["clients", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];
      const { data, error } = await supabase
        .from("clients")
        .select("id, nome")
        .eq("tenant_id", tenantId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId,
  });

  // Calcular estatísticas
  const { data: stats } = useQuery({
    queryKey: ["category-stats", categoryName, transactionItems, products],
    queryFn: async () => {
      if (!transactionItems || transactionItems.length === 0) {
        return {
          totalProducts: products.length,
          totalRevenue: 0,
          totalQuantity: 0,
          totalTransactions: 0,
          averagePrice: 0,
          averageQuantity: 0,
          maxPrice: 0,
          minPrice: 0,
          totalProfit: 0,
          profitMargin: 0,
          firstSale: null,
          lastSale: null,
          monthlySales: [],
          paymentMethods: [],
          topProducts: [],
          topClients: [],
          statusDistribution: [],
          weeklyDistribution: [],
          growthTrend: 0,
        } as CategoryStats;
      }

      const totalRevenue = transactionItems.reduce((sum, item) => sum + (item.total || 0), 0);
      const totalQuantity = transactionItems.reduce((sum, item) => sum + (item.quantidade || 0), 0);
      const totalTransactions = new Set(transactionItems.map(item => item.transaction_id)).size;
      const averagePrice = totalQuantity > 0 ? totalRevenue / totalQuantity : 0;
      const averageQuantity = totalTransactions > 0 ? totalQuantity / totalTransactions : 0;

      const prices = transactionItems.map(item => item.preco_unitario).filter(p => p > 0);
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;

      // Calcular lucro (receita - custo)
      const totalProfit = transactionItems.reduce((sum, item) => {
        const product = products.find(p => p.id === item.product_id);
        const cost = product?.preco_custo || 0;
        const profit = (item.preco_unitario - cost) * item.quantidade;
        return sum + profit;
      }, 0);
      const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

      const dates = transactionItems
        .map(item => item.transactions?.created_at)
        .filter(Boolean)
        .sort();
      const firstSale = dates.length > 0 ? dates[0] : null;
      const lastSale = dates.length > 0 ? dates[dates.length - 1] : null;

      // Vendas mensais
      const monthlyMap = new Map<string, { revenue: number; quantity: number; count: number }>();
      transactionItems.forEach(item => {
        if (!item.transactions?.created_at) return;
        const date = new Date(item.transactions.created_at);
        const monthKey = format(date, "MMM/yyyy", { locale: ptBR });
        const existing = monthlyMap.get(monthKey) || { revenue: 0, quantity: 0, count: 0 };
        monthlyMap.set(monthKey, {
          revenue: existing.revenue + item.total,
          quantity: existing.quantity + item.quantidade,
          count: existing.count + 1,
        });
      });
      const monthlySales = Array.from(monthlyMap.entries())
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => {
          const dateA = new Date(a.month.split("/")[1] + "-" + a.month.split("/")[0]);
          const dateB = new Date(b.month.split("/")[1] + "-" + b.month.split("/")[0]);
          return dateA.getTime() - dateB.getTime();
        });

      // Métodos de pagamento
      const paymentMap = new Map<string, { value: number; count: number }>();
      transactionItems.forEach(item => {
        const methodId = item.transactions?.payment_method_id;
        if (!methodId) return;
        const method = paymentMethods.find(pm => pm.id === methodId);
        const methodName = method?.nome || "Desconhecido";
        const existing = paymentMap.get(methodName) || { value: 0, count: 0 };
        paymentMap.set(methodName, {
          value: existing.value + item.total,
          count: existing.count + 1,
        });
      });
      const paymentMethodsData = Array.from(paymentMap.entries())
        .map(([method, data]) => ({ method, ...data }))
        .sort((a, b) => b.value - a.value);

      // Top produtos
      const productMap = new Map<string, { revenue: number; quantity: number }>();
      transactionItems.forEach(item => {
        const productName = item.products?.nome || "Desconhecido";
        const existing = productMap.get(productName) || { revenue: 0, quantity: 0 };
        productMap.set(productName, {
          revenue: existing.revenue + item.total,
          quantity: existing.quantity + item.quantidade,
        });
      });
      const topProducts = Array.from(productMap.entries())
        .map(([product, data]) => ({ product, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      // Top clientes
      const clientMap = new Map<string, { revenue: number; quantity: number }>();
      transactionItems.forEach(item => {
        const clientName = item.transactions?.clients?.nome;
        if (!clientName) return;
        const existing = clientMap.get(clientName) || { revenue: 0, quantity: 0 };
        clientMap.set(clientName, {
          revenue: existing.revenue + item.total,
          quantity: existing.quantity + item.quantidade,
        });
      });
      const topClients = Array.from(clientMap.entries())
        .map(([client, data]) => ({ client, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      // Distribuição por status
      const statusMap = new Map<string, { count: number; revenue: number }>();
      transactionItems.forEach(item => {
        const status = item.transactions?.status || "Desconhecido";
        const existing = statusMap.get(status) || { count: 0, revenue: 0 };
        statusMap.set(status, {
          count: existing.count + 1,
          revenue: existing.revenue + item.total,
        });
      });
      const statusDistribution = Array.from(statusMap.entries())
        .map(([status, data]) => ({ status, ...data }));

      // Distribuição semanal
      const weeklyMap = new Map<string, { revenue: number; quantity: number }>();
      transactionItems.forEach(item => {
        if (!item.transactions?.created_at) return;
        const date = new Date(item.transactions.created_at);
        const dayKey = format(date, "EEEE", { locale: ptBR });
        const existing = weeklyMap.get(dayKey) || { revenue: 0, quantity: 0 };
        weeklyMap.set(dayKey, {
          revenue: existing.revenue + item.total,
          quantity: existing.quantity + item.quantidade,
        });
      });
      const weekDays = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
      const weeklyDistribution = weekDays
        .map(day => ({
          day: day.charAt(0).toUpperCase() + day.slice(1),
          revenue: weeklyMap.get(day)?.revenue || 0,
          quantity: weeklyMap.get(day)?.quantity || 0,
        }));

      // Tendência de crescimento (comparar últimos 2 meses)
      const lastTwoMonths = monthlySales.slice(-2);
      const growthTrend = lastTwoMonths.length === 2 && lastTwoMonths[0].revenue > 0
        ? ((lastTwoMonths[1].revenue - lastTwoMonths[0].revenue) / lastTwoMonths[0].revenue) * 100
        : 0;

      return {
        totalProducts: products.length,
        totalRevenue,
        totalQuantity,
        totalTransactions,
        averagePrice,
        averageQuantity,
        maxPrice,
        minPrice,
        totalProfit,
        profitMargin,
        firstSale,
        lastSale,
        monthlySales,
        paymentMethods: paymentMethodsData,
        topProducts,
        topClients,
        statusDistribution,
        weeklyDistribution,
        growthTrend,
      } as CategoryStats;
    },
    enabled: !!categoryName && !!transactionItems,
  });

  if (categoryLoading || productsLoading || itemsLoading) {
    return (
      <div className={`container mx-auto ${isEmbedded ? 'px-2 py-2' : 'py-6'}`}>
        <div className="text-center">Carregando...</div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className={`container mx-auto ${isEmbedded ? 'px-2 py-2' : 'py-6'}`}>
        <div className="text-center">
          <p className="text-muted-foreground">Categoria não encontrada</p>
          {!isEmbedded && (
          <Button onClick={() => navigate("/categorias")} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          )}
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className={`container mx-auto ${isEmbedded ? 'px-2 py-2' : 'py-6'}`}>
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/categorias")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Categoria de Produto: {categoryName}</h1>
            {category?.descricao && (
              <p className="text-muted-foreground mt-1">{category.descricao}</p>
            )}
          </div>
        </div>
        <div className="text-center">
          <p className="text-muted-foreground">Nenhum produto encontrado para a categoria "{categoryName}"</p>
          <Button onClick={() => navigate("/categorias")} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`container mx-auto ${isEmbedded ? 'px-2 py-2 space-y-4' : 'py-6 space-y-6'}`}>
      {/* Header */}
      {!isEmbedded && (
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/categorias")}> 
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Categoria de Produto: {categoryName}</h1>
            {category?.descricao && (
              <p className="text-muted-foreground mt-1">{category.descricao}</p>
            )}
          </div>
        </div>
      )}

      {/* Cards de Estatísticas Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Produtos</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalProducts || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Total</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.totalRevenue || 0)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quantidade Vendida</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalQuantity || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Vendas</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalTransactions || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Preço Médio</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.averagePrice || 0)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lucro Total</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.totalProfit || 0)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Margem de Lucro</CardTitle>
            <Percent className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.profitMargin?.toFixed(2) || 0}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tendência</CardTitle>
            {stats && stats.growthTrend >= 0 ? (
              <TrendingUp className="h-4 w-4 text-green-600" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-600" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats && stats.growthTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats?.growthTrend?.toFixed(2) || 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs com Gráficos e Tabelas */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="charts">Gráficos</TabsTrigger>
          <TabsTrigger value="products">Produtos</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Cards Adicionais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Maior Preço</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(stats?.maxPrice || 0)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Menor Preço</CardTitle>
                <TrendingDown className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(stats?.minPrice || 0)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Primeira Venda</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-sm font-medium">
                  {stats?.firstSale ? format(new Date(stats.firstSale), "dd/MM/yyyy", { locale: ptBR }) : "—"}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Última Venda</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-sm font-medium">
                  {stats?.lastSale ? format(new Date(stats.lastSale), "dd/MM/yyyy", { locale: ptBR }) : "—"}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Produtos e Clientes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Top 10 Produtos</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead className="text-right">Receita</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.topProducts?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.revenue)}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                      </TableRow>
                    ))}
                    {(!stats?.topProducts || stats.topProducts.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground">
                          Nenhum dado disponível
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top 10 Clientes</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-right">Receita</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats?.topClients?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.client}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.revenue)}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                      </TableRow>
                    ))}
                    {(!stats?.topClients || stats.topClients.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground">
                          Nenhum dado disponível
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="charts" className="space-y-4">
          {/* Evolução de Vendas Mensal */}
          <Card>
            <CardHeader>
              <CardTitle>Evolução de Vendas Mensal</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={stats?.monthlySales || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#0088FE" name="Receita" />
                  <Line type="monotone" dataKey="quantity" stroke="#00C49F" name="Quantidade" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Distribuição por Método de Pagamento */}
          <Card>
            <CardHeader>
              <CardTitle>Distribuição por Método de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={stats?.paymentMethods || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => formatCurrency(entry.value)}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {(stats?.paymentMethods || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Distribuição Semanal */}
          <Card>
            <CardHeader>
              <CardTitle>Distribuição Semanal</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.weeklyDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#0088FE" name="Receita" />
                  <Bar dataKey="quantity" fill="#00C49F" name="Quantidade" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Distribuição por Status */}
          <Card>
            <CardHeader>
              <CardTitle>Distribuição por Status</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={stats?.statusDistribution || []}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.status}: ${entry.count}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {(stats?.statusDistribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Produtos da Categoria</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Preço de Venda</TableHead>
                    <TableHead className="text-right">Preço de Custo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="font-medium">{product.nome}</TableCell>
                      <TableCell>{product.codigo_sku || "—"}</TableCell>
                      <TableCell className="text-right">{formatCurrency(product.preco_venda)}</TableCell>
                      <TableCell className="text-right">
                        {product.preco_custo ? formatCurrency(product.preco_custo) : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={product.status === "ativo" ? "default" : "secondary"}
                          className={
                            product.status === "ativo" 
                              ? "bg-green-100 text-green-700 hover:bg-green-200" 
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }
                        >
                          {product.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(product.created_at), "dd/MM/yyyy", { locale: ptBR })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
